import { Inject, Injectable, InjectionToken } from '@angular/core';
import { Observable, Subject, Subscription, fromEvent, interval, of } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { HttpService } from 'src/app/modules/core/core/services/http.service';
import { environment } from 'src/environments/environment';

interface VersionResponse {
  version: string;
}

/** Intervalo por defecto entre chequeos de versión (5 minutos). */
const DEFAULT_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Token de inyección para `window`, exclusivamente para poder reemplazarlo
 * en tests (ver version-check.service.spec.ts). Intentar redefinir
 * `window.location` directamente en un test (`Object.defineProperty`) o
 * espiar `window.location.reload` (`spyOn`) es frágil en Chrome real: en
 * versiones recientes esa propiedad ya no es configurable, y en algunos
 * casos Karma detecta la recarga real de página y aborta toda la corrida
 * ("Some of your tests did a full page reload!"). Inyectar `window` permite
 * reemplazarlo por completo en el test sin tocar el objeto global real.
 */
export const WINDOW = new InjectionToken<Window>('WindowToken', {
  providedIn: 'root',
  factory: () => window
});

/**
 * Detecta cuándo se publicó una nueva versión del frontend mientras el usuario
 * tiene la app abierta, comparando el contenido de `/version.json` (generado
 * en cada build por el pipeline de CI) contra el valor obtenido al cargar la página.
 *
 * @remarks
 * - Solo corre en `environment.production`, ya que en desarrollo no existe `/version.json`.
 * - Chequea periódicamente (polling) y también al volver a la pestaña (`visibilitychange`),
 *   para detectar la actualización más rápido sin depender solo del intervalo.
 * - Una vez detectada una nueva versión, deja de sondear (no tiene sentido seguir
 *   preguntando ni volver a notificar).
 *
 * @example
 * ```typescript
 * // En AppComponent
 * this.versionCheckService.startPolling();
 * this.versionCheckService.updateAvailable$.subscribe(() => this.showUpdateSnackbar());
 * ```
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private currentVersion: string | null = null;
  private pollingSub?: Subscription;
  private visibilitySub?: Subscription;
  private updateDetected = false;

  private readonly updateAvailableSubject = new Subject<void>();

  /** Emite una vez que se detecta que hay una versión distinta a la cargada. */
  readonly updateAvailable$: Observable<void> = this.updateAvailableSubject.asObservable();

  constructor(
    private readonly http: HttpService,
    @Inject(WINDOW) private readonly window: Window
  ) {}

  /**
   * Inicia el sondeo de versión. Idempotente: si ya está corriendo, no hace nada.
   *
   * @param intervalMs - Frecuencia del sondeo periódico. Default: 5 minutos.
   */
  startPolling(intervalMs: number = DEFAULT_CHECK_INTERVAL_MS): void {
    if (!environment.production || this.pollingSub) {
      return;
    }

    // Fija la versión "base" con la que se cargó la app, sin disparar notificación.
    this.fetchVersion().subscribe((version) => {
      if (version) {
        this.currentVersion = version;
      }
    });

    this.pollingSub = interval(intervalMs).subscribe(() => this.checkForUpdate());

    this.visibilitySub = fromEvent(document, 'visibilitychange')
      .pipe(filter(() => document.visibilityState === 'visible'))
      .subscribe(() => this.checkForUpdate());
  }

  /** Detiene el sondeo (polling e listener de visibilidad). */
  stopPolling(): void {
    this.pollingSub?.unsubscribe();
    this.visibilitySub?.unsubscribe();
    this.pollingSub = undefined;
    this.visibilitySub = undefined;
  }

  /** Recarga la página para traer la última versión publicada. */
  reloadApp(): void {
    this.window.location.reload();
  }

  private checkForUpdate(): void {
    if (this.updateDetected || !this.currentVersion) {
      return;
    }

    this.fetchVersion().subscribe((version) => {
      if (!version || version === this.currentVersion) {
        return;
      }

      this.updateDetected = true;
      this.updateAvailableSubject.next();
      this.stopPolling();
    });
  }

  private fetchVersion(): Observable<string | null> {
    // Cache-busting: evita que el navegador o un proxy sirvan una copia vieja de
    // version.json. Se pasa como `params` (no interpolado en la URL) para que
    // `req.url` en HttpTestingController siga siendo '/version.json' limpio
    // (Angular solo separa querystring de `url` cuando se usa `params`, no
    // cuando el string ya trae un '?' embebido) — así los tests pueden matchear
    // por URL exacta sin depender del timestamp.
    return this.http
      .get<VersionResponse>('/version.json', {
        skipErrorHandling: true,
        params: { t: Date.now().toString() }
      })
      .pipe(
        map((res) => res?.version ?? null),
        catchError(() => of(null))
      );
  }
}
